import { OnDestroy } from "@angular/core";
import { StateService, TransitionService } from "@uirouter/angular";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialog } from "../../shared/confirm-dialog.component";
import { IDataService } from "../../core/base.dataservice";
import { ISettingsEntity } from "./settings.entity";
import { CrewErrorDialog } from "../../core/crew-error-dialog.component";
import { tap } from "rxjs/operators";

export abstract class EditBase<TEntity extends ISettingsEntity, TDataService extends IDataService<TEntity> = IDataService<TEntity>> implements OnDestroy {
    private unsubribe: Function;

    constructor(
        private stateService: StateService,
        transitionService: TransitionService,
        protected dialog: MatDialog,
        public entity: TEntity,
        protected dataService: TDataService,
        protected $log: ng.ILogService,
        protected isNew: boolean) {

        this.cleanUpSubscription();
        
        this.unsubribe = transitionService.onBefore({}, transition => {

            var hasChanges = this.dataService.hasChanges();
            if (hasChanges) {

                let dialogRef = this.dialog.open(ConfirmDialog);
                
                dialogRef.componentInstance.content = "There are unsaved changes, do you want to continue?";
                dialogRef
                    .afterClosed()
                    .subscribe(res => {
                        if (res) {
                            this.rejectChanges();
                            transition.run();
                        }
                    });

                return false;
            }

            return true;
        });
    }

    public abstract entityDisplayName: string;

    public abstract listStateName: string;

    private cleanUpSubscription() {
        if (this.unsubribe) {
            this.unsubribe();
            this.unsubribe = null;
        }
    }

    public ngOnDestroy() {
        this.cleanUpSubscription();
    }

    public save(): Promise<void> {
        // This takes care of the situation where the user types in a sort tag but deletes it again. We do not want to save an empty string but NULL
        if (this.entity.SortTag !== undefined && this.entity.SortTag === "") {
            this.entity.SortTag = null;
        }

        return (this.isNew ? this.saveNew() : this.saveExisting())
            .then(() => this.afterEntitySave())
            .then(() => this.$log.info("Successfully saved " + this.entityDisplayName))
            .then(() => this.backToList())
            .catch(err => this.handleError(err));
    }

    protected saveNew(): Promise<any> {
        return new Promise<breeze.SaveResult>(resolve => resolve(this.dataService.create(this.entity)));
    }

    protected saveExisting(): Promise<any> {
        return new Promise<breeze.SaveResult>(resolve => resolve(this.dataService.save(this.entity)));
    }

    protected afterEntitySave(): Promise<any> {
        return Promise.resolve();
    }

    protected handleError(err: any): Promise<void> {
        const errorMessage = "Could not save " + this.entityDisplayName;
        this.$log.error(errorMessage + ", error occured: " + err);

        const dialogRef = this.dialog.open(CrewErrorDialog);
        dialogRef.componentInstance.title = "Error occurred";
        dialogRef.componentInstance.contentStart = errorMessage;

        let mainContent = err.message ? err.message + "\n\n" : "";
        mainContent += err.longStack || err.stack ? err.longStack || err.stack : err;

        dialogRef.componentInstance.mainContent = mainContent;

        return dialogRef.afterClosed().pipe(tap(() => this.cancel())).toPromise();
    }

    private rejectChanges() {
        this.dataService.rejectChanges();
    }

    public cancel(): Promise<any> {
        this.rejectChanges();
        return this.backToList();
    }

    public backToList(): Promise<any> {
        return this.stateService.go(this.listStateName);
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/settings/shared/edit.base.ts